import { AlchemyAnalytics } from "@amzn/alchemy-components/dist/utils";

const PENDO_API_KEY = "44803214-4878-4eea-467b-b660cb9346b7";
const PENDO_ACCOUNT_ID = "Relay Yard Control";

interface VisitorData {
  id: string;
  [key: string]: any;
}

/**
 * Pendo is a SaaS tool designed to provide comprehensive insights and analytics on user engagement.
 * With Pendo, users gain deep visibility into how customers interact with their applications, empowering
 * data-driven decisions that have a positive impact on business decisions and outcomes.
 * Amazon Fulfillment Technologies Experience Design (AFT-XD) Team supports the use of Pendo to systematically
 * gather UX analytics and conduct UX benchmarking, allowing software teams to easily quantify and analyze
 * product usage and prioritize enhancements based on user impact.
 *
 * https://w.amazon.com/bin/view/AFT/XD/Pendo/
 */
export class Pendo {
  private static _instance: Pendo;
  alchemyAnalytics: AlchemyAnalytics | null = null;

  constructor() {
    if (Pendo._instance) {
      return Pendo._instance;
    }
    Pendo._instance = this;
  }

  initAnalytics = (visitorData: VisitorData | undefined): void => {
    // Skip initialization in development environment to avoid errors
    if (process.env.NODE_ENV === "development") {
      console.log("Pendo initialization skipped in development environment");
      return;
    }

    if (visitorData && visitorData.id) {
      const visitor = visitorData;

      const account = {
        id: PENDO_ACCOUNT_ID,
      };

      try {
        // Wrap in a promise and catch any errors
        Promise.resolve()
          .then(() => {
            this.alchemyAnalytics = new AlchemyAnalytics({
              apikey: PENDO_API_KEY,
              visitor,
              account,
              collectPerformanceMetrics: true,
            });
          })
          .catch((error) => {
            console.warn("Pendo initialization failed, but error was handled:", error);
          });
      } catch (error) {
        console.warn("Pendo initialization error caught:", error);
      }
    } else {
      console.warn("User is not authenticated. Skipping Pendo initialization.");
    }
  };
  /**
   * Track custom events in Pendo
   * @param eventName - Name of the custom event
   * @param properties - Optional properties to include with the event
   */
  trackEvent = (eventName: string, properties?: Record<string, unknown>): void => {
    if (this.alchemyAnalytics) {
      this.alchemyAnalytics.track(eventName, properties);
    }
  };
}

// Export singleton instance
export const pendoService = new Pendo();
